import { memo, type ReactNode } from 'react';

import { TableCell } from '~/components/TableView/TableCell';
import { rightAlignedTableCells } from '~/components/TableView/types';
import { ConfigVisibilityToggleType, getVisibilityToggleTypeFromString } from '~/constants/configViews';
import { TABLE_ROW_CELL } from '~/constants/testIDs';
import { configViewOptionsSelector } from '~/store/configViews/selectors';
import { type ConfigViewOption } from '~/store/configViews/types';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useTableView } from '../TableViewContext';
import { makeActiveTableColumnFromOption } from '../tableColumnWidthsUtils';

export type ConfigurableTableRowsProps = {
  rowInfo: Record<ConfigVisibilityToggleType, string>;
  renderCustomFieldTableCellComponent: (option: ConfigViewOption) => ReactNode;
  actorAvatar?: ReactNode;
};

export const ConfigurableTableCells = memo(
  ({ rowInfo, renderCustomFieldTableCellComponent, actorAvatar }: ConfigurableTableRowsProps) => {
    const configViewOptions = useAirSelector(configViewOptionsSelector);
    const { getColumnWidthStyle } = useTableView();

    return (
      <>
        {configViewOptions.map((option) => {
          if (option.isCustomField && option.customFieldId && option.isVisible) {
            return renderCustomFieldTableCellComponent(option);
          }

          const toggleType = getVisibilityToggleTypeFromString(option.fieldName);
          if (!toggleType || !option.isVisible) {
            return null;
          }

          const tableCellProps = {
            ['data-testid']: `${TABLE_ROW_CELL}-${option.fieldName}`,
            style: getColumnWidthStyle(
              makeActiveTableColumnFromOption(option.fieldName, option.isCustomField, option.customFieldId),
            ),
            // !important added here to override padding set in `DesktopTableRowContainer`
            className: rightAlignedTableCells.has(toggleType) ? 'text-right !pr-4' : 'text-left',
          };
          const cellValue = rowInfo[toggleType];

          if (toggleType === ConfigVisibilityToggleType.uploadedBy) {
            return (
              <TableCell key={option.fieldName} {...tableCellProps}>
                <div className="flex w-full items-center gap-2">
                  {actorAvatar}
                  <span className="truncate">{cellValue}</span>
                </div>
              </TableCell>
            );
          }

          return (
            <TableCell key={option.fieldName} {...tableCellProps}>
              {cellValue}
            </TableCell>
          );
        })}
      </>
    );
  },
);

ConfigurableTableCells.displayName = 'ConfigurableTableRows';
